import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

export default function SolutionsHero() {
  const { t } = useLanguage();

  const tags = [
    { icon: 'ri-recycle-line', label: t('循環經濟', 'Circular Economy', 'サーキュラーエコノミー') },
    { icon: 'ri-refresh-line', label: 'Plastics Reviving' },
    { icon: 'ri-bar-chart-line', label: t('高比例再生料應用', 'High Recycled Content', '高再生材料比率') },
    { icon: 'ri-leaf-line', label: t('碳足跡與碳權價值', 'Carbon Footprint & Credits', 'カーボンフットプリント') },
    { icon: 'ri-stack-line', label: t('塑膠回收再利用', 'Plastic Waste Recycling', '廃プラスチック再利用') },
  ];

  return (
    <section className="relative min-h-[580px] md:min-h-[660px] flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-top"
        style={{
          backgroundImage:
            'url(https://readdy.ai/api/search-image?query=circular%20economy%20recycled%20plastic%20products%20manufacturing%20factory%20with%20green%20sustainable%20technology%20conveyor%20belt%20recycling%20process%20industrial%20facility%20professional%20wide%20angle%20photography%20green%20and%20blue%20accent%20lighting%20B2B%20high%20resolution&width=1440&height=660&seq=solutions-hero-v1&orientation=landscape)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/42 via-black/28 to-black/12" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,184,148,0.30) 0%, transparent 65%)', zIndex: 2 }} aria-hidden="true" />
      <div className="absolute bottom-0 left-1/4 w-[450px] h-[350px] opacity-15 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(2,115,194,0.25) 0%, transparent 65%)', zIndex: 2 }} aria-hidden="true" />

      <div className="relative w-full max-w-screen-xl mx-auto px-4 md:px-6 py-20" style={{ zIndex: 3 }}>
        <div className="max-w-[720px] bg-black/20 backdrop-blur-[2px] rounded-2xl px-8 py-8 md:px-10 md:py-10">
          <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-4">
            {t('應用場景與解決方案', 'Applications & Solutions', '応用シーンとソリューション')}
          </p>
          <h1
            className="font-bold text-white mb-6"
            style={{ fontSize: 'clamp(2rem, 4.2vw, 2.75rem)', lineHeight: '1.3' }}
          >
            {t('從廢棄塑料到', 'From Waste Plastics to', '廃プラスチックから')}
            <span
              style={{
                background: 'linear-gradient(90deg, #00B894 0%, #4FC3F7 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {t('低碳高值產品', 'Low-Carbon High-Value Products', '低炭素高付加価値製品へ')}
            </span>
          </h1>
          <p className="text-white/85 mb-8 max-w-[640px]" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>
            {t(
              '聚量以 PQernel® 奈米擴鏈補強劑與 rvPlastics、rvPET、PQMB 等材料方案，串聯塑膠回收、造粒、品牌與製造端，讓高比例再生料不再只是口號，而是可落地、可量產、可被市場與法規接受的新標準。',
              'PolyQ\'s PQernel® nano chain extender and rvPlastics, rvPET, PQMB material solutions connect plastic recycling, compounding, brand owners, and manufacturers — making high recycled content not just a slogan, but an implementable, scalable, market-accepted new standard.',
              '聚量のPQernel®ナノ鎖延長補強剤とrvPlastics・rvPET・PQMBなどの材料ソリューションが、プラスチックリサイクル・造粒・ブランド・製造業者を連結します。高再生材料比率を実現可能・量産可能・市場受容可能な新標準へと変えます。'
            )}
          </p>
          <div className="flex flex-wrap gap-3 mb-9">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-green text-white text-[1.0625rem] font-semibold hover:bg-brand-green-dark transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-schedule-line"></i>
              {t('預約應用情境諮詢', 'Schedule Application Consultation', '応用シーン相談を予約')}
            </Link>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/15 border border-white/50 text-white text-[1.0625rem] font-semibold hover:bg-white/25 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-box-3-line"></i>
              {t('了解產品與材料', 'View Products & Materials', '製品・材料を見る')}
            </Link>
          </div>
          <div className="flex flex-wrap gap-2.5">
            {tags.map((tag) => (
              <div
                key={tag.label}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/25 backdrop-blur-sm"
              >
                <i className={`${tag.icon} text-brand-green text-sm`}></i>
                <span className="text-white/90 text-[0.9375rem] font-medium whitespace-nowrap">{tag.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
